import type { Metadata } from "next";

import {
  SITE_LOCALE,
  SITE_NAME,
  absoluteUrl,
  getMetadataBase,
} from "./site";
import { SHARE_IMAGES } from "./share-images";

/** Strip `**…**` for meta description / OG / Twitter (page body keeps Markdown). */
function stripMarkdownStrong(input: string | null | undefined): string {
  if (input == null) return "";
  let out = String(input);
  let prev: string;
  do {
    prev = out;
    out = out.replace(/\*\*([\s\S]*?)\*\*/g, "$1");
  } while (out !== prev);
  return out;
}

/**
 * Editorial SEO input. Shape is loose because Tina exposes both the page
 * `seo` block and the resource-only `resourceSeo` block, which carry
 * slightly different fields.
 */
export interface SeoInput {
  /**
   * Plain page title; combined with Global SEO Title (brand) as
   * `{title} | {brand}` via `resolveTitle`. Brand falls back to `SITE_NAME`.
   */
  title?: string | null;
  /** Override for resource templates that surface `seoTitle`. */
  seoTitle?: string | null;
  description?: string | null;
  /** Override for resource templates that surface `seoDescription`. */
  seoDescription?: string | null;
  keywords?: string | null;
  /** Either an absolute URL or a public path (e.g. `/meta-blog.jpg`). */
  image?: string | null;
  /**
   * When true, emit noindex and nofollow for this page (see `buildMetadata` robots).
   * Legacy content may still set `nofollow` alone; that is honored until migrated.
   */
  noindex?: boolean | null;
  /** @deprecated Tina field removed; still read for older documents. */
  nofollow?: boolean | null;
  /** Marketing-page override; legacy content may use empty string (treated as unset). */
  ogType?: "website" | "article" | "" | null;
  /** Optional trail after Binarly. Blank `url` is text only. */
  breadcrumbs?: Array<{ label?: string | null; url?: string | null }> | null;
}

/**
 * Lightweight subset of the global Tina record needed by the metadata
 * pipeline. Avoids importing generated Tina types into shared SEO code
 * so this module is consumable from server components, scripts, etc.
 */
export interface GlobalSeoInput {
  seo?: {
    title?: string | null;
    description?: string | null;
    keywords?: string | null;
    image?: string | null;
  } | null;
  organization?: {
    legalName?: string | null;
    description?: string | null;
    twitterSite?: string | null;
    twitterCreatorDefault?: string | null;
  } | null;
}

export interface ResolveShareImageOptions {
  /** Page or resource SEO (`seo` / `resourceSeo`). */
  seo?: SeoInput | null;
  /** Section landing page SEO (e.g. Blog landing for a blog post). */
  parentSeo?: SeoInput | null;
  globalSeo?: GlobalSeoInput | null;
  /** Optional hero / logo image used in JSON-LD only, after `seo.image`. */
  contentImage?: string | null;
}

export interface BuildMetadataOptions {
  seo?: SeoInput | null;
  /** Section landing page SEO when building metadata for a resource detail page. */
  parentSeo?: SeoInput | null;
  globalSeo?: GlobalSeoInput | null;
  pagePath: string;
  /** OpenGraph type. Defaults to `"website"`. */
  type?: "website" | "article";
  publishedTime?: string | null;
  modifiedTime?: string | null;
  authors?: Array<string | { name: string; url?: string | null }> | null;
  section?: string | null;
  tags?: string[] | null;
  /** Pre-built array if the caller already has typed author records. */
  category?: string | null;
}

function trimImage(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Resolves a public-path or absolute share image URL.
 * OG/Twitter: page SEO → parent landing → global → `/meta-general.png`.
 * JSON-LD may pass `contentImage` between page SEO and parent landing.
 */
export function resolveShareImagePath({
  seo,
  parentSeo,
  globalSeo,
  contentImage,
}: ResolveShareImageOptions): string {
  const raw =
    trimImage(seo?.image) ||
    trimImage(contentImage) ||
    trimImage(parentSeo?.image) ||
    trimImage(globalSeo?.seo?.image) ||
    SHARE_IMAGES.general;
  return raw;
}

/** Absolute URL variant for RSS feeds and JSON-LD. */
export function absoluteShareImageUrl(
  options: ResolveShareImageOptions,
): string {
  return absoluteUrl(resolveShareImagePath(options));
}

/** Normalize Tina datetime / ISO strings for Open Graph article times. */
export function toOpenGraphArticleDate(
  raw: string | null | undefined
): string | undefined {
  if (raw == null) return undefined;
  const trimmed = String(raw).trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? trimmed : d.toISOString();
}

const DEFAULT_DESCRIPTION =
  "Binarly's Transparency Platform offers firmware security for supply chain risk management. Gain visibility into threats and recommendations on remediation.";

function normalizePath(pagePath: string): string {
  if (!pagePath) return "/";
  if (!pagePath.startsWith("/")) return `/${pagePath}`;
  return pagePath;
}

function resolveBrand(
  globalSeo: GlobalSeoInput | null | undefined
): string {
  const fromGlobal = globalSeo?.seo?.title?.trim();
  return fromGlobal || SITE_NAME;
}

function resolveTitle(
  seo: SeoInput | null | undefined,
  globalSeo: GlobalSeoInput | null | undefined
): string {
  const brand = resolveBrand(globalSeo);
  const raw = seo?.seoTitle?.trim() || seo?.title?.trim();
  if (!raw) return brand;
  const suffix = ` | ${brand}`;
  if (raw.toLowerCase().endsWith(suffix.toLowerCase())) return raw;
  return `${raw}${suffix}`;
}

function resolveDescription(
  seo: SeoInput | null | undefined,
  globalSeo: GlobalSeoInput | null | undefined
): string {
  const raw =
    seo?.seoDescription?.trim() ||
    seo?.description?.trim() ||
    globalSeo?.seo?.description?.trim() ||
    globalSeo?.organization?.description?.trim() ||
    DEFAULT_DESCRIPTION;
  return stripMarkdownStrong(raw);
}

function resolveKeywords(
  seo: SeoInput | null | undefined,
  globalSeo: GlobalSeoInput | null | undefined
): string | undefined {
  const raw =
    seo?.keywords?.trim() || globalSeo?.seo?.keywords?.trim() || "";
  return raw ? raw : undefined;
}

function resolveImage(
  seo: SeoInput | null | undefined,
  globalSeo: GlobalSeoInput | null | undefined,
  parentSeo?: SeoInput | null
): string | undefined {
  const path = resolveShareImagePath({ seo, parentSeo, globalSeo });
  if (!path) return undefined;
  return absoluteUrl(path);
}

function normalizeAuthors(
  authors: BuildMetadataOptions["authors"]
): Array<{ name: string; url?: string }> | undefined {
  if (!authors || authors.length === 0) return undefined;
  return authors
    .map((author) => {
      if (typeof author === "string") {
        const name = author.trim();
        return name ? { name } : null;
      }
      const name = author?.name?.trim();
      if (!name) return null;
      const url = author.url?.trim();
      return url ? { name, url } : { name };
    })
    .filter((a): a is { name: string; url?: string } => a !== null);
}

/**
 * Build a complete Next.js `Metadata` object from Tina inputs.
 *
 * Programmatic responsibilities (not editor-controlled):
 * - Canonical, OG and Twitter URLs derived from `getSiteUrl()`.
 * - Twitter card type chosen based on whether an OG image is present.
 * - Plain page titles use Global SEO Title as the brand: `{pageTitle} | {brand}`.
 *   Brand comes from Global → SEO → Title, falling back to `SITE_NAME`.
 * - Share image: page SEO image, then optional parent landing SEO image,
 *   then Global Share Image, then `/meta-general.png`.
 * - Robots defaults to index/follow; editors override with `seo.noindex`
 *   ("Hide from Search Engines"), which sets both noindex and nofollow.
 *   Legacy `seo.nofollow` alone is still honored. Resources rely on the
 *   `published` flag and 404 when unpublished, so we do not accept this
 *   from `resourceSeo`.
 */
export function buildMetadata({
  seo,
  parentSeo,
  globalSeo,
  pagePath,
  type = "website",
  publishedTime,
  modifiedTime,
  authors,
  section,
  tags,
  category,
}: BuildMetadataOptions): Metadata {
  try {
    const canonicalPath = normalizePath(pagePath);
    const canonicalUrl = absoluteUrl(canonicalPath);
    const brand = resolveBrand(globalSeo);
    const title = resolveTitle(seo, globalSeo);
    const description = resolveDescription(seo, globalSeo);
    const keywords = resolveKeywords(seo, globalSeo);
    const imageUrl = resolveImage(seo, globalSeo, parentSeo);
    const ogTypeCandidate =
      typeof seo?.ogType === "string" ? seo.ogType.trim() : seo?.ogType;
    let ogType: "website" | "article";
    if (ogTypeCandidate === "article") {
      ogType = "article";
    } else if (ogTypeCandidate === "website") {
      ogType = "website";
    } else {
      ogType = type;
    }
    const hideFromSearchEngines = !!(seo?.noindex || seo?.nofollow);

    const robots = hideFromSearchEngines
      ? {
          index: false as const,
          follow: false as const,
          nocache: true,
          googleBot: {
            index: false as const,
            follow: false as const,
            noimageindex: true,
            nosnippet: true,
            noarchive: true,
          },
        }
      : {
          index: true as const,
          follow: true as const,
          googleBot: {
            index: true as const,
            follow: true as const,
            "max-image-preview": "large" as const,
            "max-snippet": -1,
            "max-video-preview": -1,
          },
        };

    const normalizedAuthors = normalizeAuthors(authors);
    const ogAuthors = normalizedAuthors?.map((a) => a.name);
    const twitterCreator =
      normalizedAuthors?.[0]?.name ||
      globalSeo?.organization?.twitterCreatorDefault ||
      undefined;
    const twitterSite =
      globalSeo?.organization?.twitterSite || twitterCreator || undefined;
    const publisher =
      globalSeo?.organization?.legalName || SITE_NAME || undefined;

    const primaryTitleForAlt =
      seo?.seoTitle?.trim() || seo?.title?.trim() || brand;

    const ogImages = imageUrl
      ? [
          {
            url: imageUrl,
            width: 1200,
            height: 630,
            alt: primaryTitleForAlt,
          },
        ]
      : [];

    const twitterImages = imageUrl ? [imageUrl] : undefined;
    const twitterCard: "summary_large_image" | "summary" = imageUrl
      ? "summary_large_image"
      : "summary";

    const metadata: Metadata = {
      metadataBase: getMetadataBase(),
      title,
      description,
      applicationName: brand,
      formatDetection: { telephone: false },
      alternates: {
        canonical: canonicalUrl,
      },
      authors: normalizedAuthors,
      creator: normalizedAuthors?.[0]?.name,
      publisher,
      keywords,
      category: category || undefined,
      openGraph: {
        type: ogType,
        siteName: brand,
        title,
        description,
        url: canonicalUrl,
        locale: SITE_LOCALE,
        images: ogImages,
        ...(ogType === "article" && {
          publishedTime: publishedTime || undefined,
          modifiedTime: modifiedTime || undefined,
          authors: ogAuthors,
          section: section || undefined,
          tags: tags || undefined,
        }),
      },
      twitter: {
        card: twitterCard,
        title,
        description,
        site: twitterSite,
        creator: twitterCreator,
        images: twitterImages,
      },
      robots,
    };

    return metadata;
  } catch (error) {
    console.error("[seo/metadata] Failed to build metadata:", error);
    return {
      title: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
    };
  }
}
